import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { HttpContext, httpResource } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CfgCellDirective } from 'src/app/common/table/table-cell.directive';
import { TableColumn, TableDataSource } from 'src/app/common/table/table-column.model';
import { HANDLED_ERROR_STATUSES, TABLE_DEFAULT_HANDLED_STATUSES } from 'src/app/common/table/table-resource';
import { CfgTableComponent } from 'src/app/common/table/table.component';
import { QueueStatusClassPipe } from 'src/app/features/synchronization/pipes/queue-status-class.pipe';
import { QueueStatusLabelPipe } from 'src/app/features/synchronization/pipes/queue-status-label.pipe';
import { SignalRService } from 'src/app/services/signalr.service';
import { environment } from 'src/environments/environment';
import {
    ApiStatusCounters,
    AppServiceState,
    ProcessStatus,
    ServiceLiveState,
    SyncPoolRowDto,
    SyncRunDto,
    SyncServiceDto,
    SyncStateDto
} from '../synchronization-center.model';
import { SynchronizationCenterService } from '../synchronization-center.service';

/** The state endpoint is cheap (a handful of aggregate queries); 5 s matches the hubs' own cadence. */
const POLL_INTERVAL_MS = 5000;

/** The hubs send every 5 s; three missed beats is a dead service, not a slow one. */
const OFFLINE_AFTER_MS = 15000;

/** QueueRecoveryHandler.StaleAfter — a kørsel quiet this long is about to be re-run. */
const STALE_AFTER_MS = 10 * 60 * 1000;

const PROCESS_STATUS_LABELS: Record<ProcessStatus, string> = {
    [ProcessStatus.Unset]: 'Ikke sat',
    [ProcessStatus.Starting]: 'Starter',
    [ProcessStatus.Running]: 'Kører',
    [ProcessStatus.InAction]: 'Arbejder',
    [ProcessStatus.Stopping]: 'Stopper',
    [ProcessStatus.Stopped]: 'Stoppet',
    [ProcessStatus.Pauseing]: 'Sætter på pause',
    [ProcessStatus.Paused]: 'På pause'
};

/** Intake is open in these states; anything else counts as stopped or on its way there. */
const INTAKE_OPEN: ReadonlySet<ProcessStatus> = new Set([ProcessStatus.Running, ProcessStatus.InAction]);
/** Online but not taking work in: stopped by us, or pausing on its own. */
const INTAKE_CLOSED: ReadonlySet<ProcessStatus> = new Set([ProcessStatus.Stopping, ProcessStatus.Stopped, ProcessStatus.Pauseing, ProcessStatus.Paused]);

/**
 * The workers send AppServiceState as a camelCased object; the API sends ApiStatusCounters as a
 * Newtonsoft-serialized PascalCase *string*. Both end up here with the keys the view reads.
 */
export function parseHubPayload(raw: unknown): Partial<AppServiceState & ApiStatusCounters> | null {
    let value: unknown = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return null;
        }
    }
    if (!value || typeof value !== 'object') return null;

    const record = value as Record<string, unknown>;
    const pick = (camel: string, pascal: string) => record[camel] ?? record[pascal];
    return {
        processingCount: pick('processingCount', 'ProcessingCount') as number | undefined,
        status: pick('status', 'Status') as ProcessStatus | undefined,
        queeuStatus: pick('queeuStatus', 'QueeuStatus') as string | undefined
    };
}

export interface ServiceView {
    role: string;
    url: string | null;
    /** 'online' | 'offline' | 'no-url' — the dot colour. */
    reach: 'online' | 'offline' | 'no-url';
    /** Reachable but closed for intake (a worker) or parking new kørsler (the API): the dot turns yellow. */
    paused: boolean;
    statusLabel: string;
    processingCount: number | null;
    lastSeen: Date | null;
    /** Which admin command would apply; null when the worker cannot be controlled or is unreachable. */
    command: 'stop' | 'start' | null;
}

/**
 * Where the sync as a whole is. 'paused' = the API parks new kørsler, workers still run;
 * 'stopping' = parked and the workers are closing, some still busy; 'stopped' = parked, every
 * worker closed, nothing running — the release-ready state; 'partial' = workers closed while the
 * API still accepts (left over from an interrupted sequence); 'unknown' = no worker can be judged.
 */
export type SyncPhase = 'unknown' | 'running' | 'paused' | 'stopping' | 'stopped' | 'partial';

/**
 * Synkroniseringscenteret: the engine's state as one page — queue pool, services, kørsler — and
 * three controls: Pause (the API), Stop (the API and every worker), Start (back to running).
 * The DB side is polled; the services' own state comes over their SignalR status hubs.
 */
@Component({
    selector: 'app-synchronization-center-state',
    templateUrl: './synchronization-center-state.component.html',
    styleUrl: './synchronization-center-state.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DatePipe, NgTemplateOutlet, RouterLink, CfgTableComponent, CfgCellDirective, QueueStatusLabelPipe, QueueStatusClassPipe]
})
export class SynchronizationCenterStateComponent {
    private readonly signalR = inject(SignalRService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly centerService = inject(SynchronizationCenterService);
    private readonly toastr = inject(ToastrService);

    /** Ticks once a second: drives the "offline" and "stale" judgements without re-fetching. */
    private readonly now = signal(Date.now());

    protected readonly state = httpResource<SyncStateDto>(() => ({
        url: `${environment.adminConfig.apiUri}/synccenter/state`,
        method: 'GET',
        withCredentials: true,
        context: new HttpContext().set(HANDLED_ERROR_STATUSES, TABLE_DEFAULT_HANDLED_STATUSES)
    }));

    protected readonly pool = computed(() => this.state.value()?.pool ?? null);
    protected readonly note = computed(() => this.state.value()?.note ?? null);
    protected readonly environmentName = computed(() => this.state.value()?.environment ?? null);
    protected readonly generatedAt = computed(() => this.state.value()?.generatedAt ?? null);
    protected readonly loadError = computed(() => this.state.error() ? 'Tilstanden kunne ikke hentes.' : null);

    /** Hub payloads by role. A plain object in a signal, replaced on every message, so OnPush sees it. */
    private readonly live = signal<Record<string, ServiceLiveState>>({});
    private readonly connected = new Set<string>();

    /** One of the three sequences is running; every button waits for it. */
    protected readonly busy = signal(false);

    protected readonly services = computed<ServiceView[]>(() => {
        const now = this.now();
        const live = this.live();
        return (this.state.value()?.services ?? []).map(service => {
            const seen = live[service.role];
            const reach: ServiceView['reach'] = !service.url
                ? 'no-url'
                : seen && now - seen.lastSeen.getTime() < OFFLINE_AFTER_MS ? 'online' : 'offline';
            const status = reach === 'online' ? seen?.status ?? null : null;
            return {
                role: service.role,
                url: service.url,
                reach,
                paused: reach === 'online' && (status !== null ? INTAKE_CLOSED.has(status) : seen?.queueState === 'paused'),
                statusLabel: reach !== 'online' || !seen
                    ? ''
                    : seen.queueState ?? (seen.status !== null ? PROCESS_STATUS_LABELS[seen.status] ?? `${seen.status}` : ''),
                processingCount: reach === 'online' ? seen?.processingCount ?? null : null,
                lastSeen: seen?.lastSeen ?? null,
                command: !service.canControl || status === null ? null : INTAKE_OPEN.has(status) ? 'stop' : 'start'
            };
        });
    });

    /** Workers that still take work in, in pipeline order (QueueMaster first); and those that do not. */
    private readonly openWorkers = computed(() => this.services().filter(s => s.command === 'stop'));
    private readonly closedWorkers = computed(() => this.services().filter(s => s.command === 'start'));

    /** The pool's state as the API itself reports it, when its hub is reachable; else what the DB says. */
    protected readonly poolState = computed(() => {
        const apiLive = this.live()['Api'];
        if (apiLive?.queueState && this.now() - apiLive.lastSeen.getTime() < OFFLINE_AFTER_MS) {
            return apiLive.queueState;
        }
        return this.pool()?.state ?? 'unknown';
    });

    protected readonly syncPhase = computed(() => {
        const controllable = this.services().filter(s => s.command !== null);
        const running = controllable.reduce((sum, s) => sum + (s.processingCount ?? 0), 0);
        const closed = this.closedWorkers().length;
        const poolPaused = this.poolState() === 'paused';

        let phase: SyncPhase;
        if (controllable.length === 0) phase = 'unknown';
        else if (poolPaused && closed === controllable.length && running === 0) phase = 'stopped';
        else if (poolPaused && closed > 0) phase = 'stopping';
        else if (poolPaused) phase = 'paused';
        else if (closed > 0) phase = 'partial';
        else phase = 'running';

        return { phase, running, closed, controllable: controllable.length, poolPaused };
    });

    protected readonly canPause = computed(() => !this.busy() && this.syncPhase().phase === 'running');
    protected readonly canStop = computed(() => !this.busy() && this.syncPhase().phase !== 'unknown' && (this.openWorkers().length > 0 || this.poolState() !== 'paused'));
    protected readonly canStart = computed(() => !this.busy() && (this.poolState() === 'paused' || this.closedWorkers().length > 0));

    protected readonly phaseBadgeClass = computed(() => {
        switch (this.syncPhase().phase) {
            case 'running': return 'text-bg-success';
            case 'paused': return 'text-bg-warning';
            case 'stopping': return 'text-bg-info';
            case 'stopped': return 'text-bg-danger';
            case 'partial': return 'text-bg-info';
            default: return 'text-bg-secondary';
        }
    });

    protected readonly phaseLabel = computed(() => {
        switch (this.syncPhase().phase) {
            case 'running': return 'Kører';
            case 'paused': return 'På pause';
            case 'stopping': return 'Stopper';
            case 'stopped': return 'Stoppet';
            case 'partial': return 'Delvist stoppet';
            default: return 'Ukendt';
        }
    });

    // ── Tables ────────────────────────────────────────────────────────────────

    protected readonly runsSource: TableDataSource<SyncRunDto> = {
        value: computed(() => this.state.value()?.runs),
        isLoading: this.state.isLoading,
        error: this.state.error,
        reload: () => this.state.reload()
    };

    protected readonly strandedSource: TableDataSource<SyncRunDto> = {
        value: computed(() => this.state.value()?.stranded),
        isLoading: this.state.isLoading,
        error: this.state.error,
        reload: () => this.state.reload()
    };

    protected readonly poolSource: TableDataSource<SyncPoolRowDto> = {
        value: computed(() => this.state.value()?.pool.items),
        isLoading: this.state.isLoading,
        error: this.state.error,
        reload: () => this.state.reload()
    };

    protected readonly hasStranded = computed(() => (this.state.value()?.stranded.length ?? 0) > 0);

    protected readonly runColumns: TableColumn<SyncRunDto>[] = [
        { key: 'customerId', header: 'Kunde', width: '90px', type: 'number' },
        { key: 'companyId', header: 'Regnskab', width: '100px', type: 'number' },
        { key: 'synchronizationName', header: 'Synkronisering', value: run => run.synchronizationName ?? run.synchronizationId },
        { key: 'task', header: 'Opgave', width: '110px', value: run => `${run.action} ${run.task}`.trim() },
        { key: 'status', header: 'Status', width: '150px', interactive: false },
        { key: 'executers', header: 'Delkørsler', width: '220px', interactive: false, sortFn: (a, b) => a.executers.total - b.executers.total },
        { key: 'lastActivity', header: 'Sidste livstegn', width: '170px', type: 'date', interactive: false },
        { key: 'created', header: 'Oprettet', width: '140px', type: 'date', interactive: false },
        { key: 'id', header: '', width: '60px', sortable: false }
    ];

    protected readonly poolColumns: TableColumn<SyncPoolRowDto>[] = [
        { key: 'customerId', header: 'Kunde', width: '90px', type: 'number' },
        { key: 'companyId', header: 'Regnskab', width: '100px', type: 'number' },
        { key: 'tableGroupId', header: 'Tabelgruppe', value: row => row.tableGroupId ?? row.partitionKey },
        { key: 'queueName', header: 'Modtager', width: '180px' },
        { key: 'status', header: 'Status', width: '130px', interactive: false },
        { key: 'added', header: 'Sat i kø', width: '150px', type: 'date', interactive: false },
        { key: 'attemptCount', header: 'Forsøg', width: '80px', type: 'number' }
    ];

    protected readonly trackByRunId = (run: SyncRunDto) => run.id;
    protected readonly trackByQueueId = (row: SyncPoolRowDto) => row.queueId;

    constructor() {
        const clock = setInterval(() => this.now.set(Date.now()), 1000);
        const poll = setInterval(() => {
            if (document.visibilityState === 'visible') this.state.reload();
        }, POLL_INTERVAL_MS);
        this.destroyRef.onDestroy(() => {
            clearInterval(clock);
            clearInterval(poll);
            for (const role of this.connected) {
                this.signalR.removeHandlers(this.hubName(role), 'status');
                this.signalR.stopConnection(this.hubName(role));
            }
        });

        // One hub connection per service, opened when the service first shows up in the payload.
        // The service list is stable between polls, so this effectively runs once per role.
        effect(() => {
            for (const service of this.state.value()?.services ?? []) {
                this.connect(service);
            }
        });
    }

    // ── The three controls ────────────────────────────────────────────────────

    /** Pause: the API parks every new kørsel as paused. The workers keep working. */
    protected async pause(): Promise<void> {
        await this.sequence(async () => {
            await this.centerService.pauseQueuePool();
            this.toastr.success('Synkroniseringen er sat på pause: nye kørsler parkeres i køen.');
        });
    }

    /**
     * Stop: Pause, then every worker closes for new work, QueueMaster first (the only dispatcher),
     * then the rest of the pipeline. What is running finishes; waiting delkørsler stay in their
     * queues. The banner reads "Stoppet" once nothing is running any more.
     */
    protected async stop(): Promise<void> {
        await this.sequence(async () => {
            if (this.poolState() !== 'paused') {
                await this.centerService.pauseQueuePool();
            }
            for (const service of this.openWorkers()) {
                await this.centerService.serviceCommand(service.role, 'stop');
            }
            this.toastr.success('Synkroniseringen stopper: det, der er i gang, gøres færdigt.');
        });
    }

    /** Start: workers open again, QueueMaster last, then the API releases the parked kørsler. */
    protected async start(): Promise<void> {
        await this.sequence(async () => {
            const closed = this.closedWorkers();
            for (const service of [...closed.filter(s => s.role !== 'QueueMaster'), ...closed.filter(s => s.role === 'QueueMaster')]) {
                await this.centerService.serviceCommand(service.role, 'start');
            }
            if (this.poolState() === 'paused') {
                await this.centerService.resumeQueuePool();
            }
            this.toastr.success('Synkroniseringen kører igen.');
        });
    }

    private async sequence(run: () => Promise<void>): Promise<void> {
        this.busy.set(true);
        try {
            await run();
            this.state.reload();
        } finally {
            this.busy.set(false);
        }
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    protected staleFor(run: SyncRunDto): boolean {
        if (!run.lastActivity) return false;
        return this.now() - new Date(run.lastActivity).getTime() > STALE_AFTER_MS;
    }

    protected rowStatusClass(status: string): string {
        switch (status) {
            case 'processing': return 'text-bg-primary';
            case 'queued': return 'text-bg-info';
            case 'paused': return 'text-bg-warning';
            default: return 'text-bg-secondary';
        }
    }

    protected rowStatusLabel(status: string): string {
        switch (status) {
            case 'processing': return 'Under behandling';
            case 'queued': return 'I kø';
            case 'paused': return 'På pause';
            default: return status;
        }
    }

    private connect(service: SyncServiceDto): void {
        if (!service.url || this.connected.has(service.role)) return;
        this.connected.add(service.role);

        const name = this.hubName(service.role);
        // The hubs are anonymous; without credentials the workers' origin-only CORS policy suffices.
        this.signalR.startConnection(name, `${service.url}/statushub`, { withCredentials: false });
        this.signalR.on<unknown>(name, 'status', raw => {
            const payload = parseHubPayload(raw);
            if (!payload) return;
            this.live.update(live => ({
                ...live,
                [service.role]: {
                    lastSeen: new Date(),
                    processingCount: payload.processingCount ?? null,
                    status: payload.status ?? null,
                    queueState: payload.queeuStatus ?? null
                }
            }));
        });
    }

    private hubName(role: string): string {
        return `synccenter-state-${role}`;
    }
}
